/**
 * The Vietnamese fallback face (ui/styles.css `Noto Sans Vi`, after Fredoka in every font stack)
 * loads only when a character Fredoka lacks renders (unicode-range). The DOM redraws once it
 * arrives; a Phaser text does not (it is drawn into its canvas once), so a scene loads the face
 * first when the text it is about to draw needs it.
 */
export const FALLBACK_FAMILY = 'Noto Sans Vi';

/** Characters only the fallback draws: Vietnamese letters and tone marks Fredoka has no glyph for (and U+1EA0–1EF9). */
const ONLY = new Set([0x0102, 0x0103, 0x0110, 0x0111, 0x0128, 0x0129, 0x0168, 0x0169, 0x01a0, 0x01a1, 0x01af, 0x01b0, 0x0300, 0x0301, 0x0302, 0x0303, 0x0309, 0x0323]);

export function needsFallback(text: string): boolean {
  for (const ch of text) {
    const c = ch.codePointAt(0)!;
    if ((c >= 0x1ea0 && c <= 0x1ef9) || ONLY.has(c)) return true;
  }
  return false;
}

/**
 * Can these texts be drawn into a canvas now: none needs the fallback face, or the face is loaded
 * for every character they need (FontFaceSet.check; no font API reads as ready).
 */
export function glyphsReady(texts: readonly string[]): boolean {
  const need = texts.filter(needsFallback).join('');
  if (!need || typeof document === 'undefined' || !document.fonts) return true;
  try {
    return [400, 700].every((w) => document.fonts.check(`${w} 16px "${FALLBACK_FAMILY}"`, need));
  } catch {
    return true;
  }
}

/** Load the fallback face when any of these texts needs it (resolves either way; never throws). */
export function loadGlyphs(texts: readonly string[]): Promise<void> {
  const need = texts.filter(needsFallback).join('');
  if (!need || typeof document === 'undefined' || !document.fonts) return Promise.resolve();
  return Promise.all([400, 700].map((w) => document.fonts.load(`${w} 16px "${FALLBACK_FAMILY}"`, need))).then(
    () => undefined,
    () => undefined,
  );
}
